import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ArrowLeft, Calculator, FileText, Shield, Clock, Users, Building2, Car, Radio, ExternalLink, Volume2, Coffee, DoorOpen, AlertTriangle, Lock, Cpu, Video, Layout, Smartphone, Mic } from "lucide-react";

// Available BioCV modules with their datasheets
const BIOCV_MODULES = [
  {
    id: 'access_control',
    name: 'ZKBioCV Access Control Module',
    description: 'Door access management with multiple credential types including Face, Palm, Card, PIN, QR Code, and Bluetooth',
    icon: Shield,
    color: 'bg-blue-500',
    datasheetUrl: 'https://customer-assets.emergentagent.com/job_security-modules-1/artifacts/6btmji0d_ZKBioCV-Access-Control-Module.pdf'
  },
  {
    id: 'audio_broadcast',
    name: 'ZKBioCV Audio Broadcast Module',
    description: 'Centralized audio announcement and broadcast system for emergency notifications and general communications',
    icon: Volume2,
    color: 'bg-pink-500',
    datasheetUrl: 'https://customer-assets.emergentagent.com/job_security-modules-1/artifacts/trdpkkg2_ZKBioCV-Audio-Broadcast-Module%20%281%29.pdf'
  },
  {
    id: 'consumption',
    name: 'ZKBioCV Consumption Module',
    description: 'Cafeteria and consumption management with meal tracking and payment integration',
    icon: Coffee,
    color: 'bg-amber-500',
    datasheetUrl: 'https://customer-assets.emergentagent.com/job_security-modules-1/artifacts/8pa8gznu_ZKBioCV-Consumption-Module.pdf'
  },
  {
    id: 'elevator_control',
    name: 'ZKBioCV Elevator Control Module',
    description: 'Floor-level access control for elevators integrated with access credentials',
    icon: Building2,
    color: 'bg-indigo-500',
    datasheetUrl: 'https://customer-assets.emergentagent.com/job_security-modules-1/artifacts/zrii45yw_ZKBioCV-Elevator-Control-Module.pdf'
  },
  {
    id: 'entrance_control',
    name: 'ZKBioCV Entrance Control Module',
    description: 'Turnstile and entrance barrier integration for controlled pedestrian access',
    icon: DoorOpen,
    color: 'bg-cyan-500',
    datasheetUrl: 'https://customer-assets.emergentagent.com/job_security-modules-1/artifacts/v9rcw91t_ZKBioCV-Entrance-Control-Module.pdf'
  },
  {
    id: 'guard_patrol',
    name: 'ZKBioCV Guard Patrol Module',
    description: 'Security patrol route management and checkpoint monitoring',
    icon: Radio,
    color: 'bg-orange-500',
    datasheetUrl: 'https://customer-assets.emergentagent.com/job_security-modules-1/artifacts/w5a3oag6_ZKBioCV-Guard-Patrol-Module.pdf'
  },
  {
    id: 'intrusion_alarm',
    name: 'ZKBioCV Intrusion Alarm',
    description: 'Integrated intrusion detection and alarm management system',
    icon: AlertTriangle,
    color: 'bg-red-500',
    datasheetUrl: 'https://customer-assets.emergentagent.com/job_security-modules-1/artifacts/z57mlb2h_ZKBioCV-Intrusion-Alarm.pdf'
  },
  {
    id: 'locker_management',
    name: 'ZKBioCV Locker Management Module',
    description: 'Smart locker allocation and access control for secure storage',
    icon: Lock,
    color: 'bg-slate-500',
    datasheetUrl: 'https://customer-assets.emergentagent.com/job_security-modules-1/artifacts/rw2undqs_ZKBioCV-Locker-Mgmt-Module.pdf'
  },
  {
    id: 'parking',
    name: 'ZKBioCV Parking Module',
    description: 'Vehicle access control, parking space management, and ANPR integration',
    icon: Car,
    color: 'bg-green-500',
    datasheetUrl: 'https://customer-assets.emergentagent.com/job_security-modules-1/artifacts/byuxcsj7_ZKBioCV-Parking-Module.pdf'
  },
  {
    id: 'personnel',
    name: 'ZKBioCV Personnel Module',
    description: 'Employee database management, organizational structure, and HR integration',
    icon: Users,
    color: 'bg-violet-500',
    datasheetUrl: 'https://customer-assets.emergentagent.com/job_security-modules-1/artifacts/p6a55ory_ZKBioCV-Personnel-Module.pdf'
  },
  {
    id: 'scene_centre',
    name: 'ZKBioCV Scene Centre Module',
    description: 'Centralized monitoring dashboard with real-time event visualization',
    icon: Cpu,
    color: 'bg-gray-600',
    datasheetUrl: 'https://customer-assets.emergentagent.com/job_security-modules-1/artifacts/wqpjulby_ZKBioCV-Scene-Centre-Module.pdf'
  },
  {
    id: 'smart_video',
    name: 'ZKBioCV Smart Video Module',
    description: 'Video surveillance integration with intelligent analytics and recording',
    icon: Video,
    color: 'bg-rose-500',
    datasheetUrl: 'https://customer-assets.emergentagent.com/job_security-modules-1/artifacts/vt045a15_ZKBioCV-Smart-Video-Module.pdf'
  },
  {
    id: 'space_management',
    name: 'ZKBioCV Space Management Module',
    description: 'Room booking, desk allocation, and workspace utilization tracking',
    icon: Layout,
    color: 'bg-sky-500',
    datasheetUrl: 'https://customer-assets.emergentagent.com/job_security-modules-1/artifacts/189rm711_ZKBioCV-Space-Mgmt-Module.pdf'
  },
  {
    id: 'time_attendance',
    name: 'ZKBioCV Time and Attendance',
    description: 'Employee time tracking with clocking points, attendance reports, and workforce management',
    icon: Clock,
    color: 'bg-teal-500',
    datasheetUrl: 'https://customer-assets.emergentagent.com/job_security-modules-1/artifacts/ym6ufan9_ZKBioCV-Time-Attendance-Module.pdf'
  },
  {
    id: 'video_intercom',
    name: 'ZKBioCV Video Intercom Module',
    description: 'Video door phone and intercom system integration for visitor verification',
    icon: Mic,
    color: 'bg-fuchsia-500',
    datasheetUrl: 'https://customer-assets.emergentagent.com/job_security-modules-1/artifacts/gdzb39rj_ZKBioCV-Video-Intercom-Module.pdf'
  },
  {
    id: 'visitor_management',
    name: 'ZKBioCV Visitor Management Module',
    description: 'Cloud-based visitor registration, check-in/out, and unmanned reception with FaceKiosk',
    icon: Users,
    color: 'bg-purple-500',
    datasheetUrl: 'https://customer-assets.emergentagent.com/job_security-modules-1/artifacts/szw6yh5t_ZKBioCV-Visitor-Management-Module.pdf'
  },
  {
    id: 'zexus_mobile',
    name: 'ZKBioCV Zexus Mobile App',
    description: 'Mobile application for remote access, notifications, and system management',
    icon: Smartphone,
    color: 'bg-emerald-500',
    datasheetUrl: 'https://customer-assets.emergentagent.com/job_security-modules-1/artifacts/2l7wynn6_ZKBioCV-Zexus-Mobile-App.pdf'
  }
];

const BioCVLanding = ({ onBack }) => {
  const navigate = useNavigate();
  const [showModulesDialog, setShowModulesDialog] = useState(false);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-8">
      <div className="max-w-5xl mx-auto">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={onBack}
          className="mb-6 text-slate-600 hover:text-slate-900"
          data-testid="back-button"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Access Control
        </Button>

        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex justify-center mb-4">
            <img src="/zkteco-logo.png" alt="ZKTeco" className="h-24 w-auto" />
          </div>
          <h1 className="text-3xl font-bold text-slate-800 mb-2">
            Bio CVSecurity
          </h1>
          <p className="text-lg text-slate-600">
            Enterprise Security Management Platform
          </p>
        </div>

        {/* Two Main Options */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          
          {/* Option 1: Bio CVSecurity Configurator */}
          <button
            onClick={() => navigate('/bio-cvsecurity-config')}
            className="group text-left"
            data-testid="biocv-configurator-card"
          >
            <Card className="border-2 border-slate-200 hover:border-blue-500 transition-all duration-300 h-72 overflow-hidden">
              <CardContent className="p-0 h-full">
                <div className="h-full bg-gradient-to-br from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800 transition-all duration-300 flex flex-col items-center justify-center text-white relative">
                  {/* Background Pattern */}
                  <div className="absolute inset-0 opacity-10">
                    <div className="absolute top-4 left-4 w-32 h-32 border-2 border-white rounded-full"></div>
                    <div className="absolute bottom-4 right-4 w-24 h-24 border-2 border-white rounded-full"></div>
                  </div>
                  
                  {/* Icon */}
                  <div className="relative z-10 mb-4">
                    <div className="w-20 h-20 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                      <Calculator className="h-10 w-10" />
                    </div>
                  </div>
                  
                  {/* Title */}
                  <h3 className="relative z-10 text-2xl font-bold mb-2 text-center px-4">Bio CVSecurity</h3>
                  <p className="relative z-10 text-sm text-white/80 text-center px-6">
                    Configure and quote Access Control, Time & Attendance, Visitor Management and more
                  </p>
                  
                  {/* Arrow indicator */}
                  <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
                    <ExternalLink className="h-5 w-5" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </button>

          {/* Option 2: Available Modules */}
          <button
            onClick={() => setShowModulesDialog(true)}
            className="group text-left"
            data-testid="biocv-modules-card"
          >
            <Card className="border-2 border-slate-200 hover:border-emerald-500 transition-all duration-300 h-72 overflow-hidden">
              <CardContent className="p-0 h-full">
                <div className="h-full bg-gradient-to-br from-emerald-600 to-teal-700 hover:from-emerald-700 hover:to-teal-800 transition-all duration-300 flex flex-col items-center justify-center text-white relative">
                  {/* Background Pattern */}
                  <div className="absolute inset-0 opacity-10">
                    <div className="absolute top-4 right-4 w-32 h-32 border-2 border-white rounded-full"></div>
                    <div className="absolute bottom-4 left-4 w-24 h-24 border-2 border-white rounded-full"></div>
                  </div>
                  
                  {/* Icon */}
                  <div className="relative z-10 mb-4">
                    <div className="w-20 h-20 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                      <FileText className="h-10 w-10" />
                    </div>
                  </div>
                  
                  {/* Title */}
                  <h3 className="relative z-10 text-2xl font-bold mb-2 text-center px-4">Available Modules</h3>
                  <p className="relative z-10 text-sm text-white/80 text-center px-6">
                    View all Bio CVSecurity modules and download product datasheets
                  </p>
                  
                  {/* Module count badge */}
                  <div className="absolute top-4 right-4">
                    <div className="px-3 py-1 rounded-full bg-white/20 backdrop-blur-sm text-xs font-semibold">
                      {BIOCV_MODULES.length} Modules
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </button>
        </div>

        {/* Available Modules Dialog */}
        <Dialog open={showModulesDialog} onOpenChange={setShowModulesDialog}>
          <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-slate-800 text-xl">
                <FileText className="h-6 w-6 text-emerald-600" />
                Bio CVSecurity Available Modules
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-3 mt-4">
              {BIOCV_MODULES.map((module) => {
                const ModuleIcon = module.icon;
                const hasDatasheet = module.datasheetUrl && module.datasheetUrl.length > 0;
                
                return (
                  <div 
                    key={module.id}
                    className="p-4 rounded-lg border border-slate-200 hover:border-slate-300 transition-colors"
                  >
                    <div className="flex items-start gap-4">
                      <div className={`w-12 h-12 rounded-lg ${module.color} flex items-center justify-center flex-shrink-0`}>
                        <ModuleIcon className="h-6 w-6 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <h4 className="font-semibold text-slate-800">{module.name}</h4>
                          {hasDatasheet ? (
                            <a
                              href={module.datasheetUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 px-3 py-1 rounded-md bg-blue-50 text-blue-600 hover:bg-blue-100 text-sm font-medium transition-colors"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <FileText className="h-4 w-4" />
                              Datasheet
                              <ExternalLink className="h-3 w-3" />
                            </a>
                          ) : (
                            <span className="px-3 py-1 rounded-md bg-slate-100 text-slate-400 text-sm">
                              Coming Soon
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-slate-600 mt-1">{module.description}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            
            <div className="mt-6 pt-4 border-t border-slate-200">
              <p className="text-xs text-slate-500 text-center">
                Click on "Datasheet" to download the product specification sheet for each module
              </p>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default BioCVLanding;
